import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline"
import React from "react"

import { formatShortDateTime } from "../../../../utils/utils"
import Badge from "../../../shared/Badge"
import { Button } from "../../../shared/Buttons"
import HealMeTooltip from "../../../shared/HealMeTooltip"
import Typography from "../../../shared/Typography"

export type PaymentGatewayReference = {
  provider: string
  referenceId: string
  referenceKind: string
  dashboardHint: string
}

export type PaymentAuditLogItem = {
  id: string
  createdAt: string
  stage: string
  status: string
  skipReason?: string | null
  errorClass?: string | null
  errorMessage?: string | null
  sourceClass?: string | null
  transactionId?: string | null
  requestId?: string | null
  displayLabel?: string | null
  displayDetail?: string | null
  displayBadgeStatus?: string | null
  displayBadgeLabel?: string | null
  practitionerVisible?: boolean | null
}

type PaymentAuditLogSectionProps = {
  paymentProcessingAuditSummary?: string | null
  paymentAuditLogs: PaymentAuditLogItem[]
  paymentGatewayReference?: PaymentGatewayReference | null
  showPackageRetry?: boolean
  onRequestPackageRetry?: () => void
  retryingPackagePayment?: boolean
  packageAmountLabel?: string | null
}

function statusBadgeType(status: string): "success" | "error" | "info" {
  if (status === "complete") return "success"
  if (status === "failed") return "error"
  return "info"
}

function providerLabel(provider: string): string {
  if (provider === "stripe") return "Stripe"
  if (provider === "square") return "Square"
  return provider
}

function referenceKindLabel(kind: string): string {
  if (kind === "payment_intent") return "Payment Intent"
  if (kind === "square_payment") return "Payment ID"
  if (kind === "square_pending") return "Pending payment"
  return "Reference"
}

function visibleLogs(logs: PaymentAuditLogItem[]): PaymentAuditLogItem[] {
  return logs.filter((log) => log.practitionerVisible !== false)
}

export default function PaymentAuditLogSection({
  paymentProcessingAuditSummary,
  paymentAuditLogs,
  paymentGatewayReference,
  showPackageRetry = false,
  onRequestPackageRetry,
  retryingPackagePayment = false,
  packageAmountLabel
}: PaymentAuditLogSectionProps) {
  const practitionerLogs = visibleLogs(paymentAuditLogs)
  const hasSummary = Boolean(paymentProcessingAuditSummary?.trim())
  const hasLogs = practitionerLogs.length > 0
  const hasGatewayReference = Boolean(paymentGatewayReference?.referenceId)
  if (!hasSummary && !hasLogs && !showPackageRetry && !hasGatewayReference) return null

  const copyReferenceId = async () => {
    if (!paymentGatewayReference?.referenceId) return
    try {
      await navigator.clipboard.writeText(paymentGatewayReference.referenceId)
    } catch {
      // Clipboard unavailable — ignore
    }
  }

  return (
    <div className="mb-6 rounded-lg border border-gray-light bg-gray-ultralight p-4">
      <Typography as="h3" variant="subtitle" className="mb-1 font-semibold">
        Payment activity
      </Typography>

      {hasGatewayReference && paymentGatewayReference && (
        <div className="mb-3 flex flex-wrap items-center gap-1 text-sm font-normal text-gray-dark">
          <span className="font-medium text-gray-dark">{providerLabel(paymentGatewayReference.provider)}</span>
          <span>·</span>
          <span>
            {referenceKindLabel(paymentGatewayReference.referenceKind)}:{" "}
            <button
              type="button"
              className="font-mono text-xs text-teal hover:underline"
              onClick={copyReferenceId}>
              {paymentGatewayReference.referenceId}
            </button>
          </span>
          <QuestionMarkCircleIcon
            className="size-4 shrink-0 text-gray-dark"
            data-tooltip-id={`payment-gateway-ref-${paymentGatewayReference.referenceId}`}
            aria-label="How to find this in your payment dashboard"
          />
          <HealMeTooltip
            id={`payment-gateway-ref-${paymentGatewayReference.referenceId}`}
            place="bottom"
            content={paymentGatewayReference.dashboardHint}
            style={{}}
          />
        </div>
      )}

      {hasSummary && (
        <div className="mb-4">
          <Typography as="p" variant="body" className="mb-1 text-xs uppercase tracking-wide text-gray-dark">
            Summary
          </Typography>
          <Typography as="p" variant="body" className="text-sm leading-snug text-gray-dark">
            {paymentProcessingAuditSummary}
          </Typography>
        </div>
      )}

      {showPackageRetry && onRequestPackageRetry && (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-orange bg-orange-light p-4 sm:flex-row sm:items-center sm:justify-between">
          <Typography as="p" variant="body" className="mb-0 text-sm text-gray-dark">
            <span className="font-bold">Package payment outstanding</span>
            {packageAmountLabel ? ` · ${packageAmountLabel}` : ""}
            <span className="mt-1 block font-normal">
              Charge the saved card for the full package purchase, not this individual session.
            </span>
          </Typography>
          <Button
            type="primary"
            size="small"
            className="shrink-0"
            disabled={retryingPackagePayment}
            floatingInMobile={false}
            style={{}}
            onClick={onRequestPackageRetry}>
            Retry package payment
          </Button>
        </div>
      )}

      {hasLogs && (
        <details className="group">
          <summary className="cursor-pointer list-none text-sm font-medium text-teal hover:underline [&::-webkit-details-marker]:hidden">
            Payment timeline ({practitionerLogs.length})
          </summary>
          <Typography as="p" variant="body" className="mt-2 text-xs text-gray-dark">
            Internal processing steps — a &ldquo;Complete&rdquo; entry at session end usually means the payment was
            already captured earlier.
          </Typography>
          <ul className="mt-3 max-h-72 space-y-3 overflow-y-auto border-t border-gray-light pt-3">
            {practitionerLogs.map((log) => {
              const badgeStatus = log.displayBadgeStatus || log.status
              const badgeLabel = log.displayBadgeLabel || log.status
              const label = log.displayLabel || log.stage
              const detail = log.displayDetail || log.skipReason

              return (
                <li key={log.id} className="border-b border-gray-light pb-3 last:border-b-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-dark">{formatShortDateTime(log.createdAt)}</span>
                    <Badge className="" type={statusBadgeType(badgeStatus)}>
                      {badgeLabel}
                    </Badge>
                    <span className="text-sm text-gray-dark">{label}</span>
                  </div>
                  {detail && (
                    <Typography as="p" variant="body" className="mt-1 text-xs text-gray-dark">
                      {detail}
                    </Typography>
                  )}
                  {(log.errorClass || log.errorMessage) && (
                    <Typography as="p" variant="body" className="mt-1 text-xs text-red">
                      {[log.errorClass, log.errorMessage].filter(Boolean).join(": ")}
                    </Typography>
                  )}
                </li>
              )
            })}
          </ul>
        </details>
      )}
    </div>
  )
}
